import axios from "axios";

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8021";


// Axios instance with 60-second timeout to allow ML prediction + SAM2 segmentation
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000
});

// Default YOLO model class for binary pest detection
export const YOLO_CLASSES = ["pest"];
export const YOLO26_CLASSES = ["pest"]; // Alias for backwards compatibility

// Fallback base64 image (small green square as fallback)
const MOCK_IMAGE_B64 = "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==";

// List of classes defaulting to YOLO model classes
let mockClasses = [...YOLO_CLASSES];

// Helper to read file as Base64 for Mock previews
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = (error) => reject(error);
  });
};

// ---- API Calls with Automatic Mock Fallback ----

// 1. Get Classes
export const getClasses = async () => {
  try {
    const res = await apiClient.get("get_classes");
    return res.data;
  } catch (err) {
    console.warn("Backend /get_classes failed or timed out, falling back to mock", err);
    return { classes: mockClasses };
  }
};

// 2. Add Label
export const addLabel = async (labelName) => {
  try {
    const res = await apiClient.post(`add_label?label_name=${encodeURIComponent(labelName)}`);
    return res.data;
  } catch (err) {
    console.warn("Backend /add_label failed or timed out, falling back to mock", err);
    if (!mockClasses.includes(labelName)) {
      mockClasses.push(labelName);
    }
    return { status: "success" };
  }
};

// 3. Crop Prediction
export const predictCrop = async (file) => {
  try {
    const formData = new FormData();
    formData.append("image", file);
    const res = await apiClient.post("crop_prediction", formData);
    return res.data;
  } catch (err) {
    console.warn("Backend /crop_prediction failed, falling back to mock", err);
    const imageB64 = await fileToBase64(file);
    const crops = ["Wheat", "Corn", "Paddy", "Soybeans", "Grapes", "Apple", "Chickpea", "Cotton"];
    const crop = crops[Math.floor(Math.random() * crops.length)];
    const isHealthy = Math.random() > 0.4;
    const diseaseName = isHealthy ? null : `${crop} Rust`;
    return {
      image: imageB64,
      detections: [
        {
          category: "crop",
          label: crop,
          confidence: 0.88 + Math.random() * 0.1,
          healthy: isHealthy,
          disease: diseaseName,
          severity: isHealthy ? "None" : (Math.random() > 0.5 ? "Moderate" : "High"),
          recommendations: isHealthy
            ? "Maintain current watering schedule. Monitor field weekly."
            : "Apply targeted fungicide treatment. Isolate the affected zone to prevent further spread."
        }
      ]
    };
  }
};

// 4. Stage Prediction
export const predictStage = async (file) => {
  try {
    const formData = new FormData();
    formData.append("image", file);
    const res = await apiClient.post("stage_prediction", formData);
    return res.data;
  } catch (err) {
    console.warn("Backend /stage_prediction failed or timed out, falling back to mock", err);
    const imageB64 = await fileToBase64(file);
    const stages = ["Seedling", "Grain Filling", "Maturity", "Harvesting"];
    const activeStageIndex = Math.floor(Math.random() * stages.length);
    const stage = stages[activeStageIndex];
    return {
      image: imageB64,
      detections: [
        {
          category: "crop_stage",
          label: stage,
          confidence: 0.90 + Math.random() * 0.08,
          activeStageIndex: activeStageIndex,
          timeline: stages,
          description: `The crop is currently in the ${stage.toLowerCase()} stage. Recommended actions include monitoring soil humidity and applying optimal nutrient inputs.`
        }
      ]
    };
  }
};

// 5. Pest & Disease Detection
export const predictPestDisease = async (file) => {
  try {
    const formData = new FormData();
    formData.append("image", file);
    const res = await apiClient.post("predict", formData);
    return res.data;
  } catch (err) {
    console.warn("Backend /predict failed or timed out, falling back to mock", err);
    const crops = ["Paddy", "Wheat", "Cotton", "Maize", "Chickpea", "Soybean"];
    const diseases = ["Blast", "Blight", "Leaf Spot", "Rust", "Healthy"];
    const mockCrop = crops[Math.floor(Math.random() * crops.length)];
    const mockDisease = diseases[Math.floor(Math.random() * diseases.length)];
    const isHealthy = mockDisease === "Healthy";
    const mockConf = (0.75 + Math.random() * 0.22).toFixed(3);

    return {
      success: true,
      prediction_id: `mock-${Date.now()}`,
      crop: mockCrop.toUpperCase(),
      disease: isHealthy ? "Healthy" : mockDisease,
      stress: isHealthy ? "None" : (Math.random() > 0.15 ? "Biotic" : "Abiotic"),
      crop_confidence: parseFloat(mockConf),
      disease_confidence: parseFloat(mockConf),
      final_label: isHealthy ? `${mockCrop}-Healthy` : `${mockCrop}-${mockDisease}`,
      symptoms: isHealthy
        ? `${mockCrop} foliage appears healthy with no visible symptoms.`
        : `${mockCrop} displaying symptoms consistent with ${mockDisease}.`,
      diagnostic_reasoning: isHealthy
        ? "No pathological symptoms detected."
        : `Visual features are consistent with ${mockDisease} on ${mockCrop}.`,
      evidence_consistency: "Strongly Supports",
      recommendations: isHealthy
        ? "Treatment: No treatment required at this time.\n\nPrevention: Continue standard monitoring, crop rotation, and field sanitation."
        : `Treatment: Apply targeted fungicide or biological control agents for ${mockDisease}.\n\nPrevention: Practice crop rotation and maintain clean tools. Remove infected plant material promptly.`
    };
  }
};

// 6. SAM/YOLO Interactive Process Image for Annotation
export const processImage = async (file, labelsMap = {}, fallbackLabel = "", conf = 0.08) => {
  try {
    const formData = new FormData();
    const cleanFileName = (file.name || "image.jpg").split("/").pop().split("\\").pop();
    formData.append("file", file, cleanFileName);
    formData.append("labels", JSON.stringify(labelsMap));
    if (fallbackLabel) {
      formData.append("fallback_label", fallbackLabel);
    }
    if (conf !== undefined && conf !== null) {
      formData.append("conf", conf);
    }
    const res = await apiClient.post("process_image", formData);
    if (res.data && Array.isArray(res.data.detections)) {
      res.data.detections = res.data.detections.map(d => ({
        ...d,
        box: d.box || d.bbox || [10, 10, 30, 30]
      }));
    }
    return res.data;
  } catch (err) {
    console.warn("Backend /process_image failed or timed out, falling back to mock", err);
    const imageB64 = await fileToBase64(file);
    const boxes = [
      { label: fallbackLabel || mockClasses[0], confidence: 0.94, box: [20, 30, 45, 60] },
      { label: mockClasses[1] || "Stem", confidence: 0.88, box: [50, 40, 75, 80] }
    ];
    return {
      overlay_b64: imageB64,
      image: (file.name || "image.jpg").split("/").pop().split("\\").pop(),
      detections: boxes,
      missing_labels: false
    };
  }
};

// 7. Process Folder API
export const processFolder = async (fileOrPath) => {
  try {
    if (typeof fileOrPath === "string") {
      const res = await apiClient.post("process_folder", { folder_path: fileOrPath });
      return res.data;
    }
    if (fileOrPath instanceof File) {
      const formData = new FormData();
      const cleanFileName = (fileOrPath.name || "image.jpg").split("/").pop().split("\\").pop();
      formData.append("file", fileOrPath, cleanFileName);
      const res = await apiClient.post("process_folder", formData);
      return res.data;
    }
  } catch (err) {
    console.warn("Backend /process_folder failed or timed out, falling back to mock", err);
    return { status: "success", detections: [] };
  }
};

// 8. Update Dataset YAML
export const updateDatasetYaml = async () => {
  try {
    const res = await apiClient.get("update_dataset_yaml");
    return res.data;
  } catch (err) {
    console.warn("Backend /update_dataset_yaml failed or timed out, falling back to mock", err);
    return { status: "success" };
  }
};

// 9. Download Annotations ZIP
export const downloadAnnotationsZip = async () => {
  try {
    const res = await fetch(`${API_BASE_URL}/download_annotations_zip`, {
      signal: AbortSignal.timeout ? AbortSignal.timeout(4000) : undefined
    });
    if (!res.ok) throw new Error("Failed to download annotations zip");
    return res.blob();
  } catch (err) {
    console.warn("Backend /download_annotations_zip failed or timed out, falling back to mock", err);
    const dummyZip = new Blob(["PK\u0005\u0006\u0000\u0000\u0000\u0000\u0000\u0000\u0000\u0000\u0000\u0000\u0000\u0000\u0000\u0000\u0000\u0000\u0000\u0000"], { type: "application/zip" });
    return dummyZip;
  }
};

// 10. Download Overlay
export const downloadOverlay = async (imageName) => {
  try {
    const res = await fetch(`${API_BASE_URL}/download_overlay/${encodeURIComponent(imageName)}`, {
      signal: AbortSignal.timeout ? AbortSignal.timeout(4000) : undefined
    });
    if (!res.ok) throw new Error("Overlay not found");
    return res.blob();
  } catch (err) {
    console.warn("Backend /download_overlay failed or timed out, falling back to mock", err);
    const dummyImage = new Blob([MOCK_IMAGE_B64], { type: "image/jpeg" });
    return dummyImage;
  }
};

// 11. Utility to compress base64 images to prevent localStorage QuotaExceededError
export const compressImage = (base64Str, maxWidth = 300, maxHeight = 300, quality = 0.5) => {
  return new Promise((resolve) => {
    if (!base64Str) return resolve("");
    // Ensure base64 prefix
    let src = base64Str;
    if (!src.startsWith("data:")) {
      src = `data:image/jpeg;base64,${src}`;
    }
    
    const img = new Image();
    img.src = src;
    img.onload = () => {
      let width = img.width;
      let height = img.height;
      
      if (width > height) {
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
      }
      
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      
      const compressed = canvas.toDataURL("image/jpeg", quality);
      resolve(compressed);
    };
    img.onerror = () => {
      resolve(base64Str);
    };
  });
};

// ---- Session API Functions ----

// Login User
export const loginUser = async (username, password) => {
  try {
    const res = await apiClient.post("login", { username, password });
    return res.data;
  } catch (err) {
    console.error("Backend login failed", err);
    throw err;
  }
};

// Validate/Refresh Session
export const validateSession = async (sessionId) => {
  try {
    const res = await apiClient.post("validate_session", { session_id: sessionId });
    return res.data;
  } catch (err) {
    console.warn("Backend session validation failed, assuming invalid", err);
    return { valid: false, reason: "Connection to server lost" };
  }
};

// Logout User
export const logoutUser = async (sessionId) => {
  try {
    const res = await apiClient.post("logout", { session_id: sessionId });
    return res.data;
  } catch (err) {
    console.warn("Backend logout failed", err);
    return { status: "success" };
  }
};

// Raise Verification Request
export const raiseVerification = async (sessionId, imageBase64, originalPrediction) => {
  try {
    const res = await apiClient.post("verification/raise", {
      session_id: sessionId,
      image: imageBase64,
      original_prediction: originalPrediction
    });
    return res.data;
  } catch (err) {
    console.error("Failed to raise verification ticket", err);
    throw err;
  }
};

// Get Verification Tickets List
export const getVerificationTickets = async (sessionId, limit = null, offset = null, status = null) => {
  try {
    const res = await apiClient.post("verification/list", { 
      session_id: sessionId,
      limit,
      offset,
      status
    });
    return res.data;
  } catch (err) {
    console.error("Failed to fetch verification tickets list", err);
    throw err;
  }
};

// Resolve Verification Ticket (Agronomist)
export const resolveVerification = async (sessionId, ticketId, validatedPrediction) => {
  try {
    const res = await apiClient.post("verification/resolve", {
      session_id: sessionId,
      ticket_id: ticketId,
      validated_prediction: validatedPrediction
    });
    return res.data;
  } catch (err) {
    console.error("Failed to resolve verification ticket", err);
    throw err;
  }
};

// Acknowledge Verification Correction (User)
export const acknowledgeVerification = async (sessionId, ticketId) => {
  try {
    const res = await apiClient.post("verification/acknowledge", {
      session_id: sessionId,
      ticket_id: ticketId
    });
    return res.data;
  } catch (err) {
    console.error("Failed to acknowledge verification correction", err);
    throw err;
  }
};

